/**
 * Error Handler - Centralni error handling za API pozive
 */
import { COMPUTE_HOURS_EXHAUSTED } from '@/constants/errorCodes';

/**
 * Custom error class for permission denied errors (HTTP 403)
 */
export class PermissionDeniedError extends Error {
  constructor(message: string = 'You do not have permission to access this resource') {
    super(message);
    this.name = 'PermissionDeniedError';
  }
}

/**
 * Specific 403 subclass thrown when the backend signals the user has
 * exhausted their compute-hours allowance. The constructor also dispatches
 * a window CustomEvent('compute-hours-exhausted') so a top-level listener
 * (mounted in AuthContext) can route to /pricing without each call site
 * needing to know about the redirect.
 */
export class ComputeHoursExhaustedError extends PermissionDeniedError {
  public readonly usedMinutes: number;
  public readonly limitMinutes: number;
  public readonly planName?: string;

  constructor(message: string, usedMinutes: number, limitMinutes: number, planName?: string) {
    super(message);
    this.name = 'ComputeHoursExhaustedError';
    this.usedMinutes = usedMinutes;
    this.limitMinutes = limitMinutes;
    this.planName = planName;
  }
}

/**
 * Custom error class for authentication errors (HTTP 401)
 */
export class AuthenticationError extends Error {
  constructor(message: string = 'Authentication required: No valid session found') {
    super(message);
    this.name = 'AuthenticationError';
  }
}

/**
 * Custom error class for not found errors (HTTP 404)
 */
export class NotFoundError extends Error {
  constructor(message: string = 'Resource not found') {
    super(message);
    this.name = 'NotFoundError';
  }
}

/**
 * Custom error class for API errors
 */
export class ApiError extends Error {
  public statusCode: number;

  constructor(message: string, statusCode: number = 500) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
  }
}

/**
 * Handle HTTP 403 Forbidden errors with user-friendly messages
 * Checks response status and throws PermissionDeniedError if 403
 *
 * @param response - Fetch Response object
 * @throws PermissionDeniedError if response status is 403
 */
export const handle403Error = async (response: Response): Promise<void> => {
  if (response.status !== 403) return;
  let body: { error?: string; error_code?: string; used_minutes?: number; limit_minutes?: number; plan?: string } = {};
  try {
    body = await response.json();
  } catch {
    // Not JSON or empty — fall through to generic permission error.
  }
  const message = body.error || 'You do not have permission to access this resource';

  if (body.error_code === COMPUTE_HOURS_EXHAUSTED) {
    window.dispatchEvent(
      new CustomEvent('compute-hours-exhausted', {
        detail: {
          usedMinutes: body.used_minutes,
          limitMinutes: body.limit_minutes,
          plan: body.plan,
        },
      })
    );
    throw new ComputeHoursExhaustedError(
      message,
      body.used_minutes ?? 0,
      body.limit_minutes ?? 0,
      body.plan,
    );
  }

  throw new PermissionDeniedError(message);
};

/**
 * Handle common HTTP errors
 *
 * @param response - Fetch Response object
 * @param context - Context string for error messages
 * @throws Appropriate error based on status code
 */
export const handleHttpError = async (response: Response, context: string = 'API call'): Promise<void> => {
  if (response.ok) return;

  // Handle 403 first
  await handle403Error(response);

  // Handle other error codes
  switch (response.status) {
    case 401:
      throw new AuthenticationError();
    case 404:
      throw new NotFoundError(`${context}: Resource not found`);
    default: {
      try {
        const data = await response.clone().json();
        throw new ApiError(data.error || `${context} failed`, response.status);
      } catch (e) {
        if (e instanceof ApiError || e instanceof PermissionDeniedError) throw e;
        throw new ApiError(`${context} failed with status ${response.status}`, response.status);
      }
    }
  }
};

/**
 * Safely log errors without exposing sensitive information
 *
 * @param context - Context string for the error
 * @param error - The error object
 */
export const logError = (context: string, error: unknown): void => {
  // Don't log AbortError - it's expected when component unmounts
  if (error instanceof Error && error.name === 'AbortError') {
    return;
  }

  console.error(`Error in ${context}:`, error instanceof Error ? error.message : error);
};

/**
 * Check if an error is an abort error (from AbortController)
 */
export const isAbortError = (error: unknown): boolean => {
  return error instanceof Error && error.name === 'AbortError';
};

/**
 * Check if an error is a permission denied error
 */
export const isPermissionDenied = (error: unknown): boolean => {
  return error instanceof PermissionDeniedError;
};

/**
 * Check if an error is an authentication error
 */
export const isAuthenticationError = (error: unknown): boolean => {
  return error instanceof AuthenticationError;
};
